import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { Ring, Spark, todayStr } from '../design/atoms';

/* SPEC 08 Progress — today's ring, streak, body composition, key metrics, achievements. */

interface OutletCtx {
  activeGymId: string | null;
  hasClassScheduling: boolean;
  loading: boolean;
}

interface MetricDef {
  id: string;
  metricKey: string;
  name: string;
  category: string | null;
  unit: string | null;
  direction: string; // 'increase' | 'decrease'
  step: number;
}

interface SeriesPoint {
  date: string;
  value: number;
}

interface BodyCompMetric {
  definition: MetricDef;
  latest: { value: number; loggedOn: string; isPr: boolean } | null;
  delta30: number | null;
  series: SeriesPoint[];
}

interface KeyMetric {
  definition: MetricDef;
  latest: { value: number; loggedOn: string; isPr: boolean } | null;
  delta30: number | null;
  isPr: boolean;
  series: SeriesPoint[];
}

interface Achievement {
  id: string;
  type: string;
  value: number | null;
  metricName: string | null;
  achievedAt: string;
}

interface OverviewData {
  date: string;
  today: { pinnedTotal: number; pinnedDone: number; pendingNames: string[]; rpe: string | null };
  streak: { current: number; best: number };
  bodyComp: { weight?: BodyCompMetric; body_fat?: BodyCompMetric };
  keyMetrics: KeyMetric[];
  achievements: Achievement[];
}

function fmtNum(v: number): string {
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}

/** Signed delta with a true minus sign: -1.2 → "−1.2", 2 → "+2" */
function fmtDelta(d: number): string {
  return `${d < 0 ? '−' : '+'}${fmtNum(Math.abs(d))}`;
}

/** A delta is "good" when it moves in the metric's improvement direction. */
function deltaIsGood(delta: number, direction: string): boolean {
  return direction === 'decrease' ? delta < 0 : delta > 0;
}

export default function MemberProgressPage() {
  const { token } = useMemberAuth();
  const { activeGymId, loading: gymsLoading } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();

  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    mfetch<{ data: OverviewData }>(token, '/member/progress/overview', { params: { date: todayStr() } })
      .then(r => setData(r.data))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load your progress'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  if (!gymsLoading && !activeGymId) {
    return (
      <div className="mpage">
        <div className="dp dp-l" style={{ paddingTop: 8 }}>My Progress</div>
        <div className="card flat"><div className="sub wrap">Join a gym to start tracking your progress.</div></div>
      </div>
    );
  }

  const isEmpty = !!data && data.today.pinnedTotal === 0 && data.keyMetrics.length === 0;
  const weight = data?.bodyComp.weight;
  const fat = data?.bodyComp.body_fat;
  const bodyMain = weight?.latest ? weight : fat?.latest ? fat : null;
  const bodySecond = bodyMain === weight && fat?.latest ? fat : null;

  return (
    <div className="mpage">
      {/* Header */}
      <div className="between" style={{ paddingTop: 8 }}>
        <div className="dp dp-l">My Progress</div>
        <div className="row" style={{ gap: 8 }}>
          {data && data.streak.current > 0 && (
            <span className="tag gold">
              <Icon n="flame" s={12} />
              {data.streak.current} day{data.streak.current === 1 ? '' : 's'}
            </span>
          )}
          <button className="icon-btn" aria-label="Plan my week" onClick={() => navigate('/app/plan/new')}>
            <Icon n="cal" s={18} c="var(--ink-2)" />
          </button>
          <button className="icon-btn" aria-label="Log today" onClick={() => navigate('/app/progress/log')}>
            <Icon n="plus" s={18} c="var(--ink-2)" />
          </button>
        </div>
      </div>

      {/* Walk-in path: start a session now and log against it as you go. */}
      <div className="card tint">
        <div className="row">
          <div className="li gold"><Icon n="flame" s={17} c="var(--gold-2)" /></div>
          <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <div className="mid">Ready to train?</div>
            <div className="tiny trunc">Pick a session template or build your own.</div>
          </div>
          <button className="btn gold sm" onClick={() => navigate('/app/activity')}>Start now</button>
        </div>
      </div>

      {loading ? (
        <>
          <div className="skel" style={{ height: 96 }} />
          <div className="skel" style={{ height: 148 }} />
          <div className="grid2">
            {[0, 1, 2, 3].map(i => <div key={i} className="skel" style={{ height: 116 }} />)}
          </div>
        </>
      ) : error ? (
        <div className="card flat">
          <div className="sub wrap">{error}</div>
          <button className="btn ghost sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      ) : data ? (
        <>
          {/* Today's log */}
          <TodayCard
            data={data}
            empty={isEmpty}
            onLog={() => navigate('/app/progress/log')}
          />

          {/* Body composition */}
          {!isEmpty && bodyMain?.latest && (
            <div className="card">
              <div className="lbl">Body composition</div>
              <div className="between" style={{ marginTop: 10, alignItems: 'flex-start' }}>
                <div className="col" style={{ gap: 6, alignItems: 'flex-start' }}>
                  <div className="row" style={{ gap: 5, alignItems: 'baseline' }}>
                    <span className="num" style={{ fontSize: 26 }}>{fmtNum(bodyMain.latest.value)}</span>
                    {bodyMain.definition.unit && <span className="sub">{bodyMain.definition.unit}</span>}
                  </div>
                  {bodyMain.delta30 != null && bodyMain.delta30 !== 0 && (
                    <span className={`tag${deltaIsGood(bodyMain.delta30, bodyMain.definition.direction) ? ' sage' : ''}`}>
                      {fmtDelta(bodyMain.delta30)} this month
                    </span>
                  )}
                </div>
                {bodySecond?.latest && (
                  <div className="col" style={{ gap: 6, alignItems: 'flex-end' }}>
                    <div className="row" style={{ gap: 5, alignItems: 'baseline' }}>
                      <span className="tiny">{bodySecond.definition.name}</span>
                      <span className="num" style={{ fontSize: 16 }}>
                        {fmtNum(bodySecond.latest.value)}{bodySecond.definition.unit ?? ''}
                      </span>
                    </div>
                    {bodySecond.delta30 != null && bodySecond.delta30 !== 0 && (
                      <span className={`tag${deltaIsGood(bodySecond.delta30, bodySecond.definition.direction) ? ' sage' : ''}`}>
                        {fmtDelta(bodySecond.delta30)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div style={{ marginTop: 12 }}>
                <Spark values={bodyMain.series.map(p => p.value)} h={48} w={320} fill />
              </div>
            </div>
          )}

          {/* Key metrics */}
          <div className="between" style={{ padding: '2px 2px 0' }}>
            <div className="lbl">Key metrics</div>
            <button
              style={{ minHeight: 44, padding: '0 2px' }}
              onClick={() => navigate('/app/progress/log')}
            >
              <span className="sub" style={{ fontWeight: 600 }}>All →</span>
            </button>
          </div>

          {isEmpty ? (
            <>
              <div className="grid2">
                {[0, 1, 2, 3].map(i => <div key={i} className="skel" style={{ height: 104 }} />)}
              </div>
              <div className="tiny" style={{ textAlign: 'center' }}>Log to see trends</div>
            </>
          ) : (
            <div className="grid2">
              {data.keyMetrics.map(m => (
                <button
                  key={m.definition.id}
                  className="card"
                  style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 5, padding: 13 }}
                  onClick={() => navigate(`/app/progress/metric/${m.definition.id}`)}
                >
                  <div className="between" style={{ gap: 6 }}>
                    <span className="mid trunc" style={{ fontSize: 13.5 }}>{m.definition.name}</span>
                    {m.isPr && <span className="tag gold">PR</span>}
                  </div>
                  {m.definition.category && <span className="tiny trunc">{m.definition.category}</span>}
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    <span className="num" style={{ fontSize: 20 }}>
                      {m.latest ? fmtNum(m.latest.value) : '—'}
                      {m.definition.unit && <span className="tiny"> {m.definition.unit}</span>}
                    </span>
                    {m.delta30 != null && m.delta30 !== 0 && deltaIsGood(m.delta30, m.definition.direction) && (
                      <span className="tag sage">{fmtDelta(m.delta30)}</span>
                    )}
                  </div>
                  <Spark values={m.series.map(p => p.value)} h={26} />
                </button>
              ))}
            </div>
          )}

          {/* Achievements */}
          {data.achievements.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {data.achievements.slice(0, 3).map(a => (
                <div key={a.id} className="card flat" style={{ padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7 }}>
                  <div className="li gold"><Icon n="medal" s={17} c="var(--gold-2)" /></div>
                  <span className="tiny trunc" style={{ maxWidth: '100%' }}>
                    {a.metricName ?? a.type.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

// ── Today's log card ─────────────────────────────────────────────────────────
function TodayCard({ data, empty, onLog }: { data: OverviewData; empty: boolean; onLog: () => void }) {
  const { pinnedDone: done, pinnedTotal: total, pendingNames } = data.today;
  const complete = total > 0 && done === total;

  if (empty) {
    return (
      <div className="card dashed">
        <div className="row">
          <div className="li gold"><Icon n="chart" s={17} c="var(--gold-2)" /></div>
          <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
            <div className="mid">Start your first log</div>
            <div className="tiny trunc">Pin a few metrics and log your first day</div>
          </div>
          <button className="btn gold sm" onClick={onLog}>Log now</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card dashed">
      <div className="row">
        {complete ? (
          <div className="li gold"><Icon n="check" s={17} c="var(--gold-2)" /></div>
        ) : (
          <Ring v={total > 0 ? (done / total) * 100 : 0} size={54} w={6}>
            <span className="num" style={{ fontSize: 13 }}>{done}/{total}</span>
          </Ring>
        )}
        <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
          <div className="mid">{complete ? 'All logged — nice' : "Today's log"}</div>
          <div className="tiny">{done} of {total} pinned metric{total === 1 ? '' : 's'}</div>
          {!complete && pendingNames.length > 0 && (
            <div className="tiny trunc">{pendingNames.join(', ')} pending</div>
          )}
        </div>
        {complete ? (
          <button className="btn ghost sm" onClick={onLog}>Edit</button>
        ) : (
          <button className="btn gold sm" onClick={onLog}>Log now</button>
        )}
      </div>
    </div>
  );
}
